import React, {
  useCallback,
  useState,
  useImperativeHandle,
  useMemo,
} from "react";
import PropTypes from "prop-types";

import BoxWithLabel from "../../box/BoxWithLabel";
import { Grid } from "@mui/material";

import { useTranslation } from "react-i18next";

import DateField from "../../field/DateField/DateField";

import EquipmentTenantRecord from "../../other/EquipmentTenantRecord/EquipmentTenantRecord";

import { isEmptyValue, isEmptyArray } from "../../../helpers/methods";

import useEquipmentService from "../../../services/equipmentService";
import AutocompleteField from "../../field/AutocompleteField";
import FloatField from "../../base/FloatField";
import { getErrorMsg } from "../../../helpers/methods";

const CounterTenantForm = React.memo(
  React.forwardRef((props, ref) => {
    const { t } = useTranslation();

    const { getEquipmentTenantFieldId } = useEquipmentService();

    const [unfillRequiredFields, setUnfillRequiredFields] = useState([]);
    const [duplicatedTenants, setDuplicatedTenants] = useState(false);
    const [invalidTenantFactors, setInvalidTenantFactors] = useState(false);

    useImperativeHandle(ref, () => ({
      checkIfRequiredFieldsAreFill, // Wartość, którą chcemy udostępnić
    }));

    const checkIfRequiredFieldsAreFill = () => {
      if (props.readOnly) return [];

      setDuplicatedTenants(false);
      setInvalidTenantFactors(false);

      let unfillFieldsTemp = [];
      let index = 0;
      for (const equipmentTenant of props.formValue) {
        if (isEmptyValue(equipmentTenant.tenant)) {
          unfillFieldsTemp.push(getEquipmentTenantFieldId(index));
        }
        if (isEmptyValue(equipmentTenant.eit_factor)) {
          unfillFieldsTemp.push(getEquipmentTenantFieldId(index, "eit_factor"));
        }
        index += 1;
      }
      setUnfillRequiredFields(unfillFieldsTemp);

      if (!isEmptyArray(unfillFieldsTemp)) {
        return ["eit_factors"];
      }

      if (
        props.formValue.reduce((sum, equipmentTenant) => {
          return sum + parseFloat(equipmentTenant.eit_factor);
        }, 0) !== 1
      ) {
        setInvalidTenantFactors(true);
        return ["eit_factors"];
      }

      if (
        props.formValue.map((equipmentTenant) => equipmentTenant.tenant)
          .length !==
        new Set(
          props.formValue.map((equipmentTenant) => equipmentTenant.tenant)
        ).size
      ) {
        setDuplicatedTenants(true);
        return ["tenants"];
      }
      return unfillFieldsTemp;
    };

    const handleDeleteTenant = useCallback((index) => {
      props.setFormValue((prev) => {
        let newFormValue = [...prev];
        if (newFormValue.length !== 1) {
          newFormValue.splice(index, 1);
        } else {
          newFormValue.splice(index, 1, {});
        }

        return newFormValue;
      });
    }, []);

    const handleAddTenant = useCallback(() => {
      props.setFormValue((prev) => [...prev, {}]);
    }, []);

    const handleChangeInArrayForm = useCallback((...inputProps) => {
      props.onChangeInArrayForm(...inputProps);
    }, []);

    const handleChangeAutocompleteFieldWithObjectOptionsInArrayForm =
      useCallback((...inputProps) => {
        props.onChangeAutocompleteFieldWithObjectOptionsInArrayForm(
          ...inputProps
        );
      }, []);

    const selectedTariffGroup = useMemo(
      () =>
        props.filteringData?.electricity_tariff_group.find(
          (etg) => etg.id === props.tariffGroup
        ),
      [props.tariffGroup]
    );

    const isEtgPowerValidForEtg = useMemo(() => {
      if (!selectedTariffGroup || props.power == null) return true;

      return !(
        props.power < selectedTariffGroup.etg_min_power ||
        props.power >
          (selectedTariffGroup?.etg_max_power || Number.MAX_SAFE_INTEGER)
      );
    }, [selectedTariffGroup, props.power]);

    const getInvalidHelperTextForEtgPower = (tariffGroup) => {
      if (!tariffGroup) return "";
      return getErrorMsg({
        error_code: t(
          `etg_power_should_be_between_[${tariffGroup.etg_min_power}]_[${
            tariffGroup.etg_max_power || ""
          }]`
        ),
      });
    };

    const formContent = (
      <Grid
        container
        rowSpacing={2}
        columnSpacing={1}
        justifyContent={"flex-end"}
      >
        <Grid item xs={12} container spacing={0.5}>
          {props.formValue.map((equipmentTenant, index) => (
            <EquipmentTenantRecord
              key={index}
              formValue={equipmentTenant}
              readOnly={props.readOnly}
              index={index}
              tenantSelectList={props.filteringData.tenant}
              showButtonAddRelation={!props.readOnly && index === 0}
              showButtonDeleteRelation={index !== 0}
              onAddRelation={handleAddTenant}
              onDeleteRelation={handleDeleteTenant}
              onChange={handleChangeInArrayForm}
              onChangeAutocompleteFieldWithObjectOptions={
                handleChangeAutocompleteFieldWithObjectOptionsInArrayForm
              }
              unfillRequiredFields={unfillRequiredFields}
              invalidTenantFactors={invalidTenantFactors}
              duplicatedTenants={duplicatedTenants}
            />
          ))}
        </Grid>
        {props.showTenantSetElectricityParams && (
          <>
            <Grid item xs={3}>
              <AutocompleteField
                name="electricity_tariff_group"
                label={t("form.meter_passport.electricity_tariff_group")}
                value={props.tariffGroup}
                options={props.filteringData?.electricity_tariff_group}
                isObjectOption={true}
                optionLabelKey={"etg_name"}
                addNewValue={false}
                multiple={false}
                onChange={props.onChangeAutocompleteFieldWithObjectOptions}
                readOnly={
                  props.readOnly ||
                  props.readOnlyFields.includes("electricity_tariff_group")
                }
                required
                error={props.unfillRequiredFields.includes(
                  "electricity_tariff_group"
                )}
                helperText={
                  props.unfillRequiredFields.includes(
                    "electricity_tariff_group"
                  ) && t("field_required")
                }
              />
            </Grid>
            <Grid item xs={3}>
              <FloatField
                name={"eits_power"}
                label={t("form.meter_passport.eits_power")}
                value={props.power}
                onChange={props.onChange}
                readOnly={
                  props.readOnly || props.readOnlyFields.includes("eits_power")
                }
                required
                helperText={
                  !isEtgPowerValidForEtg &&
                  getInvalidHelperTextForEtgPower(selectedTariffGroup)
                }
                isInvalid={!isEtgPowerValidForEtg}
              />
            </Grid>
            <Grid item xs={6}>
              <AutocompleteField
                name="ppe"
                label={t("form.meter_passport.ppe")}
                value={props.ppe}
                options={props.filteringData?.ppe}
                isObjectOption={true}
                optionLabelKey={"ppe_number"}
                addNewValue={false}
                multiple={false}
                onChange={props.onChangeAutocompleteFieldWithObjectOptions}
                readOnly={
                  props.readOnly || props.readOnlyFields.includes("ppe")
                }
                required
                error={props.unfillRequiredFields.includes("ppe")}
                helperText={
                  props.unfillRequiredFields.includes("ppe") &&
                  t("field_required")
                }
              />
            </Grid>
            <Grid item xs={12}>
              <AutocompleteField
                name="eits_parent"
                label={t("form.meter_passport.eits_parent")}
                value={props.eitsParent}
                options={props.filteringData?.eits_parent}
                isObjectOption={true}
                optionLabelKey={"eits_description"}
                addNewValue={false}
                multiple={false}
                onChange={props.onChangeAutocompleteFieldWithObjectOptions}
                readOnly={
                  props.readOnly || props.readOnlyFields.includes("eits_parent")
                }
                required
                error={props.unfillRequiredFields.includes("eits_parent")}
                helperText={
                  props.unfillRequiredFields.includes("eits_parent") &&
                  t("field_required")
                }
              />
            </Grid>
          </>
        )}
        {props.showStartSettlement && (
          <Grid item xs={6}>
            <DateField
              name={"eits_start_settlement"}
              label={t("form.meter_passport.eits_start_settlement")}
              value={props.startSettlement}
              onChange={props.onChangeDate}
              showClearIcon={false}
              maxDate={props.endSettlement ? props.endSettlement : null}
              readOnly={
                props.readOnly ||
                props.readOnlyFields.includes("eits_start_settlement")
              }
              required
              helperText={
                props.unfillRequiredFields.includes("eits_start_settlement") &&
                t("field_required")
              }
              error={props.unfillRequiredFields.includes(
                "eits_start_settlement"
              )}
            />
          </Grid>
        )}
        {props.showEndSettlement && (
          <Grid item xs={6}>
            <DateField
              name={"eits_end_settlement"}
              label={t("form.meter_passport.eits_end_settlement")}
              value={props.endSettlement}
              onChange={props.onChangeDate}
              minDate={props.startSettlement ? props.startSettlement : null}
              showClearIcon={false}
              readOnly={
                props.readOnly ||
                props.readOnlyFields.includes("eits_end_settlement")
              }
              required
              helperText={
                props.unfillRequiredFields.includes("eits_end_settlement") &&
                t("field_required")
              }
              error={props.unfillRequiredFields.includes("eits_end_settlement")}
            />
          </Grid>
        )}
      </Grid>
    );

    return props.inBox ? (
      <BoxWithLabel label={t("form.meter_passport.tenant")}>
        {formContent}
      </BoxWithLabel>
    ) : (
      formContent
    );
  })
);

CounterTenantForm.propTypes = {
  formValue: PropTypes.object,
  setFormValue: PropTypes.func,
  onChangeDate: PropTypes.func,
  onChangeInArrayForm: PropTypes.func,
  onChangeAutocompleteFieldWithObjectOptionsInArrayForm: PropTypes.func,
  readOnly: PropTypes.bool,
  filteringData: PropTypes.object,
  readOnlyFields: PropTypes.bool,
  unfillRequiredFields: PropTypes.array,
  showStartSettlement: PropTypes.bool,
  showEndSettlement: PropTypes.bool,
  showTenantSetElectricityParams: PropTypes.bool,
  inBox: PropTypes.bool,
};

CounterTenantForm.defaultProps = {
  readOnly: false,
  readOnlyFields: [],
  showStartSettlement: true,
  showEndSettlement: false,
  showTariffGroup: true,
  showPower: true,
  showPPE: true,
  inBox: true,
  showTenantSetElectricityParams: false,
};

export default CounterTenantForm;
